// ══════════════════════════════════════════════════════════════════════════════
// MESSAGERIE UNIFIÉE COCKPIT PARTICULIER - VERSION PRODUCTION
// Connectée au backend réel - Messages éphémères 24h - Notes vocales listen-once
// Style harmonisé avec Liste de tâches
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Send, MoreVertical, Phone, Video, Image, Paperclip,
  Check, CheckCheck, Clock, Star, Archive, Trash2,
  Mic, Smile, Plus, ArrowLeft, Ban, X, Filter, Users,
  HardHat, ShoppingBag, Megaphone, BadgeCheck, MessageCircle,
  Sparkles, FileText, Play, Pause, StopCircle, MicOff, Languages, User,
  Home, Heart, Briefcase, Dumbbell, ChevronRight, Mail, UserPlus, UserCheck, UserX
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import TranslateButton from '../../components/messaging/TranslateButton';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ══════════════════════════════════════════════════════════════════════════════
// HOOKS & UTILS
// ══════════════════════════════════════════════════════════════════════════════

const getToken = () => {
  // Essayer buyer_token d'abord (authAPI)
  const buyerToken = localStorage.getItem('buyer_token');
  if (buyerToken) return buyerToken;
  
  // Sinon client_token
  const clientToken = localStorage.getItem('client_token');
  if (clientToken) return clientToken;
  
  // Ancien format clientUser
  const clientUser = localStorage.getItem('clientUser');
  if (clientUser) {
    try {
      const user = JSON.parse(clientUser);
      return user.token;
    } catch (e) {}
  }
  
  return localStorage.getItem('authToken');
};

const formatTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  
  if (diff < 60000) return "À l'instant";
  if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`;
  if (diff < 86400000) return `Il y a ${Math.floor(diff / 3600000)}h`;
  if (diff < 172800000) return 'Hier';
  
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const calculateExpiresIn = (expiresAt) => {
  if (!expiresAt) return null;
  const expires = new Date(expiresAt);
  const now = new Date();
  const diff = expires - now;
  
  if (diff <= 0) return 'Expiré';
  
  const hours = Math.floor(diff / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  
  if (hours > 0) return `${hours}h`;
  return `${minutes}min`;
};

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT LISTE DES CONVERSATIONS
// ══════════════════════════════════════════════════════════════════════════════

function ConversationList({ conversations, selectedId, onSelect, loading, onSearch, searchQuery }) {
  // Tous les filtres sur une seule ligne
  const filters = [
    { id: 'all', label: 'Toutes', type: 'status' },
    { id: 'unread', label: 'Non lues', type: 'status' },
    { id: 'personnel', label: 'Personnel', type: 'category' },
    { id: 'pros', label: 'Pros', type: 'status' },
    { id: 'famille', label: 'Famille', type: 'category' },
    { id: 'amis', label: 'Amis', type: 'category' },
    { id: 'travail', label: 'Travail', type: 'category' },
    { id: 'sport', label: 'Sport', type: 'category' },
    { id: 'groups', label: 'Groupes', type: 'status' },
  ];
  
  const [activeFilter, setActiveFilter] = useState('all');
  const [showScrollHint, setShowScrollHint] = useState(true);
  const scrollContainerRef = useRef(null);

  // Masquer l'indicateur de scroll après le premier scroll
  const handleScroll = () => {
    if (showScrollHint) setShowScrollHint(false);
  };

  const filteredConversations = conversations.filter(conv => {
    // Filtres de statut
    if (activeFilter === 'unread') return conv.unread_count > 0;
    if (activeFilter === 'groups') return conv.type === 'team';
    if (activeFilter === 'pros') return conv.participants?.some(p => p.user_type === 'professionnel');
    
    // Filtres de catégorie
    if (['personnel', 'famille', 'amis', 'travail', 'sport'].includes(activeFilter)) {
      return conv.category === activeFilter;
    }
    
    return true;
  });

  const unreadCount = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Recherche - Optimisée mobile Samsung S24/S25 */}
      <div className="flex-shrink-0 px-3 pt-3 pb-2">
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-100">
          <Search size={14} className="text-gray-400 flex-shrink-0" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Rechercher..."
            className="flex-1 bg-transparent border-0 outline-none text-xs text-gray-800 placeholder-gray-400"
          />
        </div>
      </div>

      {/* Filtres - Une seule ligne scrollable avec indicateur */}
      <div className="flex-shrink-0 px-3 pb-3">
        <div className="relative flex items-center">
          <div 
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-1 pr-6"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {filters.map(filter => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                  activeFilter === filter.id
                    ? 'bg-orange-500 text-white shadow-sm'
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <span className="flex items-center gap-1">
                  {filter.label}
                  {filter.id === 'unread' && unreadCount > 0 && (
                    <span className={`min-w-[18px] h-[18px] flex items-center justify-center text-[10px] rounded-full ${
                      activeFilter === filter.id 
                        ? 'bg-white/25 text-white' 
                        : 'bg-orange-500 text-white'
                    }`}>
                      {unreadCount}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>
          
          {/* Flèche orange discrète - parfaitement alignée avec les filtres */}
          {showScrollHint && (
            <div className="flex-shrink-0 flex items-center pointer-events-none">
              <ChevronRight size={18} className="text-orange-500" strokeWidth={2.5} />
            </div>
          )}
        </div>
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-[#1a2a4a] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="text-center py-12 px-4">
            <MessageCircle size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">Aucune conversation</p>
            <p className="text-gray-400 text-sm mt-1">Les messages apparaîtront ici</p>
          </div>
        ) : (
          filteredConversations.map((conv) => {
            const otherUser = conv.participants?.[0] || {};
            const displayName = conv.group_name || otherUser.display_name || 'Utilisateur';
            const avatar = conv.group_avatar || otherUser.avatar;
            const isSelected = conv.id === selectedId;
            
            return (
              <div
                key={conv.id}
                onClick={() => onSelect(conv)}
                className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                  isSelected ? 'bg-[#1a2a4a]/5' : 'hover:bg-gray-50'
                }`}
                data-testid={`conversation-${conv.id}`}
              >
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  {avatar ? (
                    <img src={avatar} alt="" className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold"
                      style={{ backgroundColor: conv.type === 'team' ? '#8B5CF6' : '#E8650A' }}
                    >
                      {displayName.charAt(0).toUpperCase()}
                    </div>
                  )}
                  {otherUser.is_online && (
                    <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900 truncate">{displayName}</span>
                      {conv.type === 'team' && (
                        <span className="px-1.5 py-0.5 text-[10px] font-bold bg-purple-100 text-purple-600 rounded">
                          Team
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {formatRelativeTime(conv.last_message_at)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <p className="text-sm text-gray-500 truncate pr-2">
                      {conv.last_message_text || 'Démarrer une conversation'}
                    </p>
                    {conv.unread_count > 0 && (
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#E8650A] text-white text-xs font-bold flex items-center justify-center">
                        {conv.unread_count > 9 ? '9+' : conv.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT ENREGISTREMENT VOCAL
// ══════════════════════════════════════════════════════════════════════════════

function VoiceRecorder({ onSend, onCancel }) {
  const [recording, setRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setRecording(true);
      
      timerRef.current = setInterval(() => {
        setDuration(d => {
          if (d >= 60) {
            stopRecording();
            return 60;
          }
          return d + 1;
        });
      }, 1000);
    } catch (error) {
      toast.error('Impossible d\'accéder au microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const handleSend = () => {
    if (audioBlob) {
      onSend(audioBlob, duration);
    }
  };

  const handleCancel = () => {
    setAudioBlob(null);
    setDuration(0);
    onCancel();
  };

  useEffect(() => {
    startRecording();
    return () => {
      clearInterval(timerRef.current);
      if (mediaRecorderRef.current && recording) {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex items-center gap-3 p-3 bg-red-50 rounded-xl">
      <button onClick={handleCancel} className="p-2 hover:bg-red-100 rounded-full">
        <X size={20} className="text-red-500" />
      </button>
      
      <div className="flex-1 flex items-center gap-3">
        <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
        <span className="text-red-600 font-mono">{formatDuration(duration)}</span>
        <div className="flex-1 h-1 bg-red-200 rounded overflow-hidden">
          <div 
            className="h-full bg-red-500 transition-all"
            style={{ width: `${(duration / 60) * 100}%` }}
          />
        </div>
      </div>

      {recording ? (
        <button 
          onClick={stopRecording}
          className="p-2.5 bg-red-500 hover:bg-red-600 rounded-full"
        >
          <StopCircle size={20} className="text-white" />
        </button>
      ) : (
        <button 
          onClick={handleSend}
          className="p-2.5 bg-green-500 hover:bg-green-600 rounded-full"
        >
          <Send size={20} className="text-white" />
        </button>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT LECTEUR AUDIO (Note vocale)
// ══════════════════════════════════════════════════════════════════════════════

function VoicePlayer({ url, duration, isListened, messageId, onListened }) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);

  const handlePlay = async () => {
    if (!audioRef.current) return;
    
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      try {
        await audioRef.current.play();
        setPlaying(true);
      } catch (error) {
        toast.error('Impossible de lire l\'audio');
      }
    }
  };

  const handleEnded = async () => {
    setPlaying(false);
    setProgress(0);
    
    // Marquer comme écouté (suppression côté serveur)
    if (!isListened && onListened) {
      onListened(messageId);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current && audioRef.current.duration) {
      setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
    }
  };

  if (isListened) {
    return (
      <div className="flex items-center gap-2 text-gray-400 italic text-sm">
        <MicOff size={16} />
        <span>Note vocale écoutée · supprimée</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 min-w-[200px]">
      <audio
        ref={audioRef}
        src={url}
        onEnded={handleEnded}
        onTimeUpdate={handleTimeUpdate}
      />
      
      <button 
        onClick={handlePlay}
        className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
      >
        {playing ? (
          <Pause size={18} className="text-white" />
        ) : (
          <Play size={18} className="text-white ml-0.5" />
        )}
      </button>
      
      <div className="flex-1">
        <div className="h-1 bg-white/30 rounded overflow-hidden">
          <div 
            className="h-full bg-white transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="text-xs text-white/70 mt-1">
          {duration ? `${Math.floor(duration / 60)}:${(duration % 60).toString().padStart(2, '0')}` : '0:00'}
        </span>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT ZONE DE CHAT
// ══════════════════════════════════════════════════════════════════════════════

function ChatArea({ conversation, messages, loading, onSendMessage, onBack, onVoiceListened, currentUserId }) {
  const [message, setMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [aiQuotas, setAiQuotas] = useState(null);
  const [autoTranslateLanguages, setAutoTranslateLanguages] = useState([]);
  const [translations, setTranslations] = useState({}); // {messageId: translationData}
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Charger les quotas IA
    const loadQuotas = async () => {
      try {
        const token = getToken();
        const res = await fetch(`${API_URL}/api/ai/quotas`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          setAiQuotas(await res.json());
        }
      } catch (e) {}
    };
    loadQuotas();
    
    // Charger les préférences de traduction
    const loadTranslationPrefs = async () => {
      try {
        const token = getToken();
        const res = await fetch(`${API_URL}/api/translate/preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const prefs = await res.json();
          setAutoTranslateLanguages(prefs.auto_translate_from || []);
        }
      } catch (e) {}
    };
    loadTranslationPrefs();
  }, []);

  const handleSend = async () => {
    if (!message.trim()) return;
    
    await onSendMessage({ type: 'text', content: message.trim() });
    setMessage('');
    inputRef.current?.focus();
  };

  const handleVoiceSend = async (blob, duration) => {
    setIsRecording(false);
    
    // Upload du fichier audio
    const formData = new FormData();
    formData.append('file', blob, 'voice.webm');
    
    try {
      const token = getToken();
      const uploadRes = await fetch(`${API_URL}/api/messages/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (uploadRes.ok) {
        const { url } = await uploadRes.json();
        await onSendMessage({
          type: 'voice',
          content: '🎤 Note vocale',
          file_url: url,
          voice_duration: duration
        });
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du vocal');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = getToken();
      const uploadRes = await fetch(`${API_URL}/api/messages/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (uploadRes.ok) {
        const { url } = await uploadRes.json();
        const isImage = file.type.startsWith('image/');
        
        await onSendMessage({
          type: isImage ? 'image' : 'file',
          content: isImage ? '🖼️ Image' : `📎 ${file.name}`,
          file_url: url,
          file_name: file.name,
          file_size: file.size,
          file_mime_type: file.type
        });
      }
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    }
    
    e.target.value = '';
  };

  const handleCorrection = async () => {
    if (!message.trim()) return;
    
    if (aiQuotas && aiQuotas.correction_remaining <= 0 && !aiQuotas.has_premium) {
      toast.error('Tu as utilisé tes 20 corrections gratuites ce mois-ci. Active tes Superpouvoirs !');
      return;
    }

    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/ai/correct?text=${encodeURIComponent(message)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setMessage(data.corrected_text);
          toast.success(`✨ Texte corrigé (${data.remaining} restantes)`);
          setAiQuotas(prev => prev ? { ...prev, correction_remaining: data.remaining } : null);
        } else {
          toast.error(data.message || 'Erreur de correction');
        }
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleContextMenu = (e, msg) => {
    e.preventDefault();
    setContextMenu({
      x: Math.min(e.clientX, window.innerWidth - 200),
      y: Math.min(e.clientY, window.innerHeight - 200),
      message: msg
    });
  };

  const handleSaveToNotes = async (msg) => {
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/notes/from-message?message_id=${msg.id}&folder=Personnel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Message enregistré dans tes Notes');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
    setContextMenu(null);
  };

  const otherUser = conversation.participants?.[0] || {};
  const displayName = conversation.group_name || otherUser.display_name || 'Conversation';

  return (
    <div className="flex-1 flex flex-col h-full bg-white overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-100 flex items-center gap-3 px-4 py-3 z-10">
        <button 
          onClick={onBack}
          className="lg:hidden p-2.5 hover:bg-gray-100 rounded-full -ml-2"
        >
          <ArrowLeft size={22} className="text-gray-700" />
        </button>
        
        <div 
          className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
          style={{ backgroundColor: conversation.type === 'team' ? '#8B5CF6' : '#E8650A' }}
        >
          {otherUser.avatar ? (
            <img src={otherUser.avatar} alt="" className="w-full h-full rounded-full object-cover" />
          ) : (
            displayName.charAt(0).toUpperCase()
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900 truncate">{displayName}</h3>
            {conversation.type === 'team' && (
              <span className="px-1.5 py-0.5 text-[10px] font-bold bg-purple-100 text-purple-600 rounded">
                Team
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500">
            {otherUser.is_online ? 'En ligne' : 'Hors ligne'}
          </p>
        </div>
        
        <div className="flex items-center gap-1">
          <button 
            onClick={() => toast.info('📞 Demande d\'appel envoyée')}
            className="p-2.5 hover:bg-gray-100 rounded-full"
          >
            <Phone size={18} className="text-gray-500" />
          </button>
          <button 
            onClick={() => toast.info('📹 Demande de visio envoyée')}
            className="p-2.5 hover:bg-gray-100 rounded-full"
          >
            <Video size={18} className="text-gray-500" />
          </button>
          <div className="relative">
            <button 
              onClick={() => setShowMoreMenu(!showMoreMenu)}
              className="p-2.5 hover:bg-gray-100 rounded-full"
            >
              <MoreVertical size={18} className="text-gray-500" />
            </button>
            
            <AnimatePresence>
              {showMoreMenu && (
                <>
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-40"
                    onClick={() => setShowMoreMenu(false)}
                  />
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border z-50"
                  >
                    <button className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left">
                      <Star size={16} className="text-amber-500" />
                      <span className="text-sm">Favoris</span>
                    </button>
                    <button className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left">
                      <Archive size={16} className="text-gray-500" />
                      <span className="text-sm">Archiver</span>
                    </button>
                    <button className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 text-left">
                      <Ban size={16} className="text-red-500" />
                      <span className="text-sm text-red-600">Bloquer</span>
                    </button>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Bannière Messages Éphémères */}
      <div 
        className="flex-shrink-0 px-4 py-2 flex items-center justify-center gap-2"
        style={{ background: 'rgba(26,42,74,0.06)' }}
      >
        <span style={{ fontSize: '11px', color: '#1a2a4a', opacity: 0.6 }}>
          💬 Messages éphémères · Supprimés après 24h · Zéro donnée sauvegardée
        </span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-[#1a2a4a] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12">
            <MessageCircle size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">Démarrer la conversation</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isMe = msg.is_mine;
            const expiresIn = calculateExpiresIn(msg.expires_at);
            
            return (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
              >
                <div 
                  className="max-w-[75%] group"
                  onContextMenu={(e) => handleContextMenu(e, msg)}
                >
                  {/* Nom expéditeur pour les groupes */}
                  {!isMe && conversation.type === 'team' && (
                    <p className="text-xs font-medium mb-1 text-purple-600">
                      {msg.sender_name}
                    </p>
                  )}
                  
                  {/* Bulle de message */}
                  <div
                    className={`px-4 py-2.5 rounded-2xl ${
                      isMe
                        ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-br-md'
                        : 'bg-gray-100 text-gray-900 rounded-bl-md'
                    }`}
                  >
                    {msg.type === 'voice' ? (
                      <VoicePlayer
                        url={msg.file_url}
                        duration={msg.voice_duration}
                        isListened={msg.is_voice_listened}
                        messageId={msg.id}
                        onListened={onVoiceListened}
                      />
                    ) : msg.type === 'image' ? (
                      <img 
                        src={msg.file_url} 
                        alt="" 
                        className="max-w-full rounded-lg cursor-pointer"
                        onClick={() => window.open(msg.file_url, '_blank')}
                      />
                    ) : msg.type === 'file' ? (
                      <a 
                        href={msg.file_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 hover:underline"
                      >
                        <Paperclip size={16} />
                        <span>{msg.file_name || 'Fichier'}</span>
                      </a>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    )}
                  </div>
                  
                  {/* Indicateurs */}
                  <div className={`flex items-center gap-1.5 mt-1 ${isMe ? 'justify-end' : ''}`}>
                    <span className="text-[10px] text-gray-400">{formatTime(msg.created_at)}</span>
                    {isMe && (
                      msg.is_read 
                        ? <CheckCheck size={12} className="text-blue-500" />
                        : <Check size={12} className="text-gray-400" />
                    )}
                    {expiresIn && !msg.is_transaction_message && (
                      <span className="text-[10px]" style={{ color: 'rgba(0,0,0,0.35)' }}>
                        · Supprimé dans {expiresIn}
                      </span>
                    )}
                  </div>
                  
                  {/* Bouton Traduire - seulement pour les messages des autres et de type texte */}
                  {!isMe && msg.type === 'text' && msg.content && msg.content.length > 3 && (
                    <TranslateButton
                      text={msg.content}
                      messageId={msg.id}
                      userLang="fr"
                      autoTranslateLanguages={autoTranslateLanguages}
                      onTranslated={(id, data) => {
                        setTranslations(prev => ({ ...prev, [id]: data }));
                      }}
                      onAutoTranslateChange={setAutoTranslateLanguages}
                    />
                  )}
                </div>
              </motion.div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Menu contextuel */}
      <AnimatePresence>
        {contextMenu && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[10002]"
              onClick={() => setContextMenu(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="fixed z-50 bg-white rounded-2xl shadow-xl overflow-hidden"
              style={{ 
                top: contextMenu.y,
                left: contextMenu.x,
                minWidth: '200px'
              }}
            >
              <button 
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left"
                onClick={() => handleSaveToNotes(contextMenu.message)}
              >
                <FileText size={16} className="text-blue-500" />
                <span className="text-sm">Enregistrer dans Notes</span>
              </button>
              <button 
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 text-left border-t"
                onClick={() => {
                  toast.success('Message supprimé');
                  setContextMenu(null);
                }}
              >
                <Trash2 size={16} className="text-red-500" />
                <span className="text-sm text-red-600">Supprimer</span>
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Input Area */}
      <div 
        className="flex-shrink-0 bg-gray-100 px-3 py-2.5"
        style={{ paddingBottom: 'max(10px, env(safe-area-inset-bottom))' }}
      >
        <input 
          type="file" 
          ref={fileInputRef} 
          onChange={handleFileUpload} 
          className="hidden" 
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
        />
        
        {isRecording ? (
          <VoiceRecorder 
            onSend={handleVoiceSend}
            onCancel={() => setIsRecording(false)}
          />
        ) : (
          <div className="flex items-center gap-2">
            <button 
              onClick={() => fileInputRef.current?.click()}
              className="p-2 hover:bg-gray-200 rounded-full"
            >
              <Plus size={22} className="text-gray-600" />
            </button>
            
            <div className="flex-1 flex items-center bg-white rounded-full px-4 py-2.5">
              <input
                ref={inputRef}
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Message..."
                className="flex-1 bg-transparent border-0 focus:ring-0 text-base outline-none"
              />
              
              {/* Bouton Correction IA */}
              {message.trim().length > 0 && (
                <button 
                  onClick={handleCorrection}
                  className="ml-1 p-1.5 hover:bg-purple-100 rounded-full"
                  title={`Corriger (${aiQuotas?.correction_remaining || 0} restantes)`}
                >
                  <Sparkles size={18} className="text-purple-500" />
                </button>
              )}
              
              <button className="ml-1">
                <Smile size={22} className="text-gray-400" />
              </button>
            </div>
            
            {message.trim() ? (
              <motion.button
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                onClick={handleSend}
                className="p-2.5 rounded-full"
                style={{ backgroundColor: '#25D366' }}
              >
                <Send size={20} className="text-white" />
              </motion.button>
            ) : (
              <button 
                onClick={() => setIsRecording(true)}
                className="p-2.5 rounded-full"
                style={{ backgroundColor: '#25D366' }}
              >
                <Mic size={20} className="text-white" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════

export default function MessagerieParticulierPage({ onAskAbra, onBackToHub }) {
  const { user } = useCurrentUser();
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  
  // Détection mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isDirectContact, setIsDirectContact] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [searchContact, setSearchContact] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [newMessageCategory, setNewMessageCategory] = useState('personnel');
  const isDarkMode = false;

  // ═══════════════════════════════════════════════════════════════════════════
  // INVITATIONS - État et logique
  // ═══════════════════════════════════════════════════════════════════════════
  const [showInvitationsPopup, setShowInvitationsPopup] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [processingInvitation, setProcessingInvitation] = useState(null);

  // Charger les invitations
  const loadInvitations = useCallback(async () => {
    setLoadingInvitations(true);
    try {
      const token = getToken();
      if (!token) {
        setLoadingInvitations(false);
        return;
      }

      const res = await fetch(`${API_URL}/api/messages/invitations`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setInvitations(data.invitations || []);
      } else {
        // Invitations de démo si pas d'API
        setInvitations([
          { 
            id: 'inv1', 
            sender: { 
              id: 'u1', 
              name: 'Marie Dupont', 
              avatar: null, 
              email: 'marie@example.com',
              user_type: 'particulier'
            }, 
            message: 'Salut ! J\'aimerais discuter avec toi sur Abracada Chat.',
            created_at: new Date(Date.now() - 3600000).toISOString() 
          },
          { 
            id: 'inv2', 
            sender: { 
              id: 'u2', 
              name: 'Pierre Martin', 
              avatar: null, 
              email: 'pierre@pro.com',
              user_type: 'professionnel'
            }, 
            message: 'Bonjour, je souhaite te contacter concernant mes services.',
            created_at: new Date(Date.now() - 86400000).toISOString() 
          },
        ]);
      }
    } catch (error) {
      console.error('Error loading invitations:', error);
      // Fallback avec données de démo
      setInvitations([
        { 
          id: 'inv1', 
          sender: { 
            id: 'u1', 
            name: 'Marie Dupont', 
            avatar: null, 
            email: 'marie@example.com',
            user_type: 'particulier'
          }, 
          message: 'Salut ! J\'aimerais discuter avec toi sur Abracada Chat.',
          created_at: new Date(Date.now() - 3600000).toISOString() 
        },
      ]);
    } finally {
      setLoadingInvitations(false);
    }
  }, []);

  // Accepter une invitation
  const handleAcceptInvitation = async (invitationId) => {
    setProcessingInvitation(invitationId);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/messages/invitations/${invitationId}/accept`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Invitation acceptée ! Une nouvelle conversation a été créée.');
        setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
        loadConversations(); // Recharger pour afficher la nouvelle conversation
        if (invitations.length <= 1) {
          setShowInvitationsPopup(false);
        }
      } else {
        // Simulation locale si pas d'API
        toast.success('Invitation acceptée !');
        setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
        if (invitations.length <= 1) {
          setShowInvitationsPopup(false);
        }
      }
    } catch (error) {
      // Simulation locale
      toast.success('Invitation acceptée !');
      setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
      if (invitations.length <= 1) {
        setShowInvitationsPopup(false);
      }
    } finally {
      setProcessingInvitation(null);
    }
  };

  // Refuser une invitation
  const handleRefuseInvitation = async (invitationId) => {
    setProcessingInvitation(invitationId);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/messages/invitations/${invitationId}/refuse`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.info('Invitation refusée');
        setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
        if (invitations.length <= 1) {
          setShowInvitationsPopup(false);
        }
      } else {
        // Simulation locale
        toast.info('Invitation refusée');
        setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
        if (invitations.length <= 1) {
          setShowInvitationsPopup(false);
        }
      }
    } catch (error) {
      // Simulation locale
      toast.info('Invitation refusée');
      setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
      if (invitations.length <= 1) {
        setShowInvitationsPopup(false);
      }
    } finally {
      setProcessingInvitation(null);
    }
  };

  // Charger les invitations au montage
  useEffect(() => {
    loadInvitations();
  }, [loadInvitations]);

  // Catégories pour le nouveau message
  const messageCategories = [
    { id: 'personnel', label: 'Personnel', icon: Home, color: 'bg-blue-500' },
    { id: 'famille', label: 'Famille', icon: Heart, color: 'bg-pink-500' },
    { id: 'amis', label: 'Amis', icon: Users, color: 'bg-purple-500' },
    { id: 'travail', label: 'Travail', icon: Briefcase, color: 'bg-amber-500' },
    { id: 'sport', label: 'Sport', icon: Dumbbell, color: 'bg-green-500' },
  ];

  // Charger les contacts quand la modale s'ouvre, reset à la fermeture
  useEffect(() => {
    if (showCreateModal) {
      loadContacts();
    } else {
      setIsDirectContact(false);
    }
  }, [showCreateModal]);

  // État pour détecter le clavier mobile
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  
  // Détection du clavier via visualViewport API
  useEffect(() => {
    if (!showCreateModal || !isMobile) {
      setKeyboardHeight(0);
      return;
    }
    
    // Masquer la navbar quand le modal est ouvert
    document.body.classList.add('modal-fullscreen-open');
    
    const viewport = window.visualViewport;
    if (!viewport) return;
    
    const handleViewportResize = () => {
      const heightDiff = window.innerHeight - viewport.height;
      setKeyboardHeight(heightDiff > 50 ? heightDiff : 0);
    };
    
    viewport.addEventListener('resize', handleViewportResize);
    viewport.addEventListener('scroll', handleViewportResize);
    
    return () => {
      document.body.classList.remove('modal-fullscreen-open');
      viewport.removeEventListener('resize', handleViewportResize);
      viewport.removeEventListener('scroll', handleViewportResize);
    };
  }, [showCreateModal, isMobile]);

  const loadContacts = async () => {
    setLoadingContacts(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContacts(data.contacts || []);
      } else {
        // Contacts de démo si pas d'API
        setContacts([
          { id: '1', name: 'Marie Dupont', email: 'marie@example.com', phone: '06 12 34 56 78' },
          { id: '2', name: 'Pierre Martin', email: 'pierre@example.com', phone: '06 98 76 54 32' },
          { id: '3', name: 'Sophie Bernard', email: 'sophie@example.com', phone: '06 11 22 33 44' },
        ]);
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
      setContacts([
        { id: '1', name: 'Marie Dupont', email: 'marie@example.com', phone: '06 12 34 56 78' },
        { id: '2', name: 'Pierre Martin', email: 'pierre@example.com', phone: '06 98 76 54 32' },
        { id: '3', name: 'Sophie Bernard', email: 'sophie@example.com', phone: '06 11 22 33 44' },
      ]);
    } finally {
      setLoadingContacts(false);
    }
  };

  const filteredContacts = contacts.filter(c => 
    c.name?.toLowerCase().includes(searchContact.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchContact.toLowerCase())
  );

  const handleSendNewMessage = async () => {
    if (!selectedContact || !newMessage.trim()) {
      toast.error('Sélectionne un contact et écris un message');
      return;
    }

    try {
      const token = getToken();
      // Créer une nouvelle conversation
      const res = await fetch(`${API_URL}/api/messages/conversations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          recipient_id: selectedContact.id,
          message: newMessage,
          category: newMessageCategory
        })
      });

      if (res.ok) {
        toast.success('Message envoyé !');
        setShowCreateModal(false);
        setSelectedContact(null);
        setNewMessage('');
        setNewMessageCategory('personnel');
        setSearchContact('');
        loadConversations();
      } else {
        toast.error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Charger les conversations
  const loadConversations = useCallback(async () => {
    setLoadingConversations(true);
    try {
      const token = getToken();
      if (!token) {
        setLoadingConversations(false);
        return;
      }

      const res = await fetch(`${API_URL}/api/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setConversations(data.conversations || []);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoadingConversations(false);
    }
  }, []);

  // Charger les messages d'une conversation
  const loadMessages = useCallback(async (conversationId) => {
    setLoadingMessages(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/messages/conversations/${conversationId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Ouvre la nouvelle conversation si ?to= est dans l'URL (depuis DevisPage, ChantiersPage…)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const toId = params.get('to');
    const toName = params.get('name');
    if (toId) {
      setSelectedContact({ id: toId, name: decodeURIComponent(toName || 'Artisan'), email: '' });
      setNewMessage('Bonjour, je vous contacte au sujet de ma demande de devis. Pouvez-vous me recontacter ?');
      setIsDirectContact(true);
      setShowCreateModal(true);
    }
  }, [location.search]);

  // Sélectionner une conversation
  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    setShowChat(true);
    loadMessages(conv.id);
  };

  // Envoyer un message
  const handleSendMessage = async (messageData) => {
    if (!selectedConversation) return;

    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/messages/conversations/${selectedConversation.id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(messageData)
      });

      if (res.ok) {
        // Recharger les messages
        loadMessages(selectedConversation.id);
        // Mettre à jour la liste des conversations
        loadConversations();
      } else {
        toast.error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Marquer un vocal comme écouté
  const handleVoiceListened = async (messageId) => {
    try {
      const token = getToken();
      await fetch(`${API_URL}/api/messages/messages/${messageId}/voice-listened`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Mettre à jour localement
      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, is_voice_listened: true, file_url: null }
          : msg
      ));
    } catch (error) {
      console.error('Error marking voice as listened:', error);
    }
  };

  // Retour à la liste
  const handleBack = () => {
    setShowChat(false);
    setSelectedConversation(null);
  };

  // Récupérer l'ID de l'utilisateur courant
  const getCurrentUserId = () => {
    if (user?.id) return user.id;
    
    const buyerUser = localStorage.getItem('buyer_user');
    if (buyerUser) {
      try {
        return JSON.parse(buyerUser).id;
      } catch (e) {}
    }
    
    const clientUser = localStorage.getItem('client_user');
    if (clientUser) {
      try {
        return JSON.parse(clientUser).id;
      } catch (e) {}
    }
    
    return null;
  };
  
  const currentUserId = getCurrentUserId();
  const unreadCount = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  return (
    <div className="h-full flex flex-col">
      {/* Header - Style Liste de tâches */}
      <div className={isMobile ? 'mb-2' : 'mb-4'}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Flèche retour vers le Hub - Mobile uniquement */}
            {isMobile && onBackToHub && (
              <button 
                onClick={onBackToHub}
                className="p-1 -ml-1"
                data-testid="back-to-hub-btn"
              >
                <ArrowLeft size={20} className={isDarkMode ? 'text-white' : 'text-[#1e3a5f]'} strokeWidth={2} />
              </button>
            )}
            <div>
              <h1 className={`${isMobile ? 'text-[16px]' : 'text-xl'} font-bold ${isDarkMode ? 'text-white' : 'text-[#1e3a5f]'}`}>
                Mes messages
              </h1>
              {!isMobile && (
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Toutes tes conversations au même endroit
                </p>
              )}
            </div>
          </div>
          {isMobile ? (
            <button 
              onClick={() => setShowCreateModal(true)}
              data-testid="create-message-btn"
              className="flex items-center gap-1 px-2.5 py-1.5 bg-[#F97316] text-white rounded-lg font-semibold text-[11px] whitespace-nowrap"
            >
              Nouveau message
            </button>
          ) : (
            <Button 
              onClick={() => setShowCreateModal(true)}
              data-testid="create-message-btn"
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl gap-2 h-9 px-4 text-sm font-semibold shadow-sm"
            >
              <Plus size={16} />
              Nouveau message
            </Button>
          )}
        </div>

        {/* Stats rapides */}
        <div className="flex items-center gap-3 mt-3 flex-wrap">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
            isDarkMode ? 'bg-slate-800' : 'bg-white border border-gray-100'
          }`}>
            <MessageCircle size={14} className="text-orange-500" />
            <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {conversations.length} conversation{conversations.length > 1 ? 's' : ''}
            </span>
          </div>
          {unreadCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-orange-50 border border-orange-100">
              <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
              <span className="text-xs font-medium text-orange-700">
                {unreadCount} non lu{unreadCount > 1 ? 's' : ''}
              </span>
            </div>
          )}
          {/* Compteur d'invitations cliquable */}
          <button
            onClick={() => setShowInvitationsPopup(true)}
            data-testid="invitations-counter-btn"
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all ${
              invitations.length > 0 
                ? 'bg-[#1e3a5f] text-white hover:bg-[#2d4a6f] shadow-sm' 
                : 'bg-white border border-gray-100 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Mail size={14} className={invitations.length > 0 ? 'text-orange-400' : 'text-gray-400'} />
            <span className="text-xs font-medium">
              {invitations.length} invitation{invitations.length > 1 ? 's' : ''}
            </span>
            {invitations.length > 0 && (
              <span className="ml-1 w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
            )}
          </button>
        </div>
      </div>

      {/* Contenu principal - Layout existant */}
      <div className={`flex-1 rounded-2xl overflow-hidden flex ${
        isDarkMode ? 'bg-slate-800' : 'bg-white border border-gray-100'
      }`}>
        {/* Liste des conversations */}
        <div className={`${showChat ? 'hidden lg:flex' : 'flex'} w-full lg:w-[380px] lg:border-r border-gray-200 flex-col`}>
          <ConversationList
            conversations={conversations}
            selectedId={selectedConversation?.id}
            onSelect={handleSelectConversation}
            loading={loadingConversations}
            onSearch={setSearchQuery}
            searchQuery={searchQuery}
          />
        </div>

        {/* Zone de chat */}
        <div className={`${showChat ? 'flex' : 'hidden lg:flex'} flex-1 flex-col`}>
          {selectedConversation ? (
            <ChatArea
              conversation={selectedConversation}
              messages={messages}
              loading={loadingMessages}
              onSendMessage={handleSendMessage}
              onBack={handleBack}
              onVoiceListened={handleVoiceListened}
              currentUserId={currentUserId}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center bg-gray-50">
              <div className="text-center p-8 max-w-sm">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-gray-100">
                  <MessageCircle size={28} className="text-gray-300" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Tes messages</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Sélectionne une conversation ou envoie un nouveau message
                </p>
                <Button 
                  onClick={() => setShowCreateModal(true)}
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl gap-2"
                >
                  <Plus size={16} />
                  Nouveau message
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal Nouveau message - Version mobile plein écran / Desktop centré */}
      <AnimatePresence>
        {showCreateModal && (
          isMobile ? (
            /* ═══ VERSION MOBILE - PLEIN ÉCRAN ═══ */
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className="fixed inset-0 z-[10002] flex flex-col bg-white"
            >
              {/* Header */}
              <header className="flex items-center gap-2 px-3 py-3 border-b border-gray-100">
                <button 
                  onClick={() => setShowCreateModal(false)}
                  className="p-1 text-gray-500"
                >
                  <ArrowLeft size={22} />
                </button>
                <h1 className="text-[15px] font-bold text-gray-900">Nouveau message</h1>
              </header>

              {/* Contenu scrollable */}
              <div className="flex-1 overflow-y-auto p-5">
                {/* Contact sélectionné (toujours en tête) */}
                {selectedContact && (
                  <div className="mb-4 flex items-center gap-3 p-3 bg-orange-50 rounded-xl border border-orange-200">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#1e3a5f] to-[#2d4a6f] rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                      {selectedContact.name?.charAt(0) || 'A'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500">Destinataire</p>
                      <p className="text-sm font-semibold text-gray-900 truncate">{selectedContact.name}</p>
                    </div>
                    {!isDirectContact && (
                      <button onClick={() => setSelectedContact(null)} className="p-1.5 hover:bg-orange-100 rounded-full">
                        <X size={14} className="text-orange-600" />
                      </button>
                    )}
                  </div>
                )}

                {/* Recherche de contact (masquée en mode contact direct) */}
                {!isDirectContact && (
                  <>
                    <div className="mb-4">
                      <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">
                        Destinataire *
                      </label>
                      <div className="flex items-center gap-2 px-3 py-3 border border-gray-200 rounded-xl bg-gray-50">
                        <Search size={14} className="text-gray-400 flex-shrink-0" />
                        <input
                          type="text"
                          value={searchContact}
                          onChange={(e) => setSearchContact(e.target.value)}
                          placeholder="Rechercher un contact..."
                          className="flex-1 bg-transparent border-0 outline-none text-sm text-gray-700 placeholder-gray-400"
                        />
                      </div>
                    </div>
                    <div className="mb-4 border border-gray-200 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                      {loadingContacts ? (
                        <div className="flex justify-center py-6">
                          <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                      ) : filteredContacts.length === 0 ? (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          {searchContact ? 'Aucun contact trouvé' : 'Aucun contact enregistré'}
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {filteredContacts.map((contact) => (
                            <button key={contact.id} type="button" onClick={() => setSelectedContact(contact)}
                              className={`w-full p-3 flex items-center gap-3 text-left transition-colors ${
                                selectedContact?.id === contact.id ? 'bg-orange-50 border-l-4 border-orange-500' : 'hover:bg-gray-50'
                              }`}>
                              <div className="w-10 h-10 bg-gradient-to-br from-[#1e3a5f] to-[#2d4a6f] rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                                {contact.name?.charAt(0) || 'C'}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm text-gray-900 truncate">{contact.name}</p>
                                <p className="text-xs text-gray-500 truncate">{contact.email || contact.phone}</p>
                              </div>
                              {selectedContact?.id === contact.id && <Check size={18} className="text-orange-500 flex-shrink-0" />}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Message */}
                <div className="mb-4">
                  <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">
                    Ton message *
                  </label>
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Écris ton message ici..."
                    rows={isDirectContact ? 5 : 4}
                    className="w-full p-4 rounded-xl border text-[15px] resize-none bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-500/10"
                  />
                </div>

                {/* Sélecteur de catégorie */}
                {!isDirectContact && (
                  <div className="mb-4">
                    <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">
                      Catégorie
                    </label>
                    <div className="flex gap-1.5 overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                      {messageCategories.map(cat => {
                        const Icon = cat.icon;
                        const isActive = newMessageCategory === cat.id;
                        return (
                          <button key={cat.id} type="button" onClick={() => setNewMessageCategory(cat.id)}
                            className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5 ${
                              isActive ? 'bg-orange-500 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200'
                            }`}>
                            <Icon size={14} />
                            <span>{cat.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer fixe - Se positionne au-dessus du clavier */}
              <div 
                className="p-4 border-t border-gray-100 bg-white transition-all duration-200"
                style={{ 
                  paddingBottom: keyboardHeight > 0 ? '16px' : 'calc(16px + env(safe-area-inset-bottom, 0px))',
                  marginBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0px'
                }}
              >
                {/* Bouton Créer avec Abracada */}
                <button
                  onClick={() => {
                    if (onAskAbra) {
                      onAskAbra({ contact: selectedContact, message: newMessage, isNew: true });
                      setShowCreateModal(false);
                    } else {
                      toast.info('Demande à Abra de t\'aider à rédiger ton message !');
                    }
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-sm font-medium rounded-xl transition-all text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200"
                >
                  <Sparkles size={16} />
                  Créer avec Abracada
                </button>
                
                {/* Boutons Annuler et Envoyer */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 h-12 rounded-xl font-semibold transition-all border border-gray-300 text-[#1e3a5f] hover:bg-gray-100"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSendNewMessage}
                    disabled={!selectedContact || !newMessage.trim()}
                    className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 transition-all disabled:opacity-50"
                  >
                    Envoyer
                  </button>
                </div>
              </div>
            </motion.div>
          ) : (
            /* ═══ VERSION DESKTOP - Modal centré ═══ */
            <div className="fixed inset-0 md:left-[288px] z-[10002] flex items-center justify-center p-4 bg-black/60">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
              >
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                  <h3 className="text-lg font-semibold text-gray-900">Nouveau message</h3>
                  <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                    <X size={20} className="text-gray-500" />
                  </button>
                </div>

                <div className="p-6 space-y-4 overflow-y-auto">
                  {/* Contact sélectionné (toujours en tête) */}
                  {selectedContact && (
                    <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-xl border border-orange-200">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#1e3a5f] to-[#2d4a6f] rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                        {selectedContact.name?.charAt(0) || 'A'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500">Destinataire</p>
                        <p className="text-sm font-semibold text-gray-900 truncate">{selectedContact.name}</p>
                      </div>
                      {!isDirectContact && (
                        <button onClick={() => setSelectedContact(null)} className="ml-auto p-1 hover:bg-orange-100 rounded-full">
                          <X size={14} className="text-orange-600" />
                        </button>
                      )}
                    </div>
                  )}

                  {/* Recherche de contact (masquée en mode contact direct) */}
                  {!isDirectContact && (
                    <>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          Destinataire *
                        </label>
                        <div className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-xl">
                          <Search size={14} className="text-gray-400 flex-shrink-0" />
                          <input type="text" value={searchContact} onChange={(e) => setSearchContact(e.target.value)}
                            placeholder="Rechercher un contact..."
                            className="flex-1 bg-transparent border-0 outline-none text-xs text-gray-700 placeholder-gray-400" />
                        </div>
                      </div>
                      <div className="border border-gray-200 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                        {loadingContacts ? (
                          <div className="flex justify-center py-6">
                            <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                          </div>
                        ) : filteredContacts.length === 0 ? (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            {searchContact ? 'Aucun contact trouvé' : 'Aucun contact enregistré'}
                          </div>
                        ) : (
                          <div className="divide-y divide-gray-100">
                            {filteredContacts.map((contact) => (
                              <button key={contact.id} type="button" onClick={() => setSelectedContact(contact)}
                                className={`w-full p-3 flex items-center gap-3 text-left transition-colors ${
                                  selectedContact?.id === contact.id ? 'bg-orange-50 border-l-4 border-orange-500' : 'hover:bg-gray-50'
                                }`}>
                                <div className="w-10 h-10 bg-gradient-to-br from-[#1e3a5f] to-[#2d4a6f] rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                                  {contact.name?.charAt(0) || 'C'}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm text-gray-900 truncate">{contact.name}</p>
                                  <p className="text-xs text-gray-500 truncate">{contact.email || contact.phone}</p>
                                </div>
                                {selectedContact?.id === contact.id && <Check size={18} className="text-orange-500 flex-shrink-0" />}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Message */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Ton message *
                    </label>
                    <textarea value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Écris ton message ici..." rows={isDirectContact ? 6 : 4}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 resize-none" />
                  </div>

                  {/* Sélecteur de catégorie (masqué en mode direct) */}
                  {!isDirectContact && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Catégorie
                      </label>
                      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                        {messageCategories.map(cat => {
                          const Icon = cat.icon;
                          const isActive = newMessageCategory === cat.id;
                          return (
                            <button key={cat.id} type="button" onClick={() => setNewMessageCategory(cat.id)}
                              className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5 ${
                                isActive ? `${cat.color} text-white shadow-sm` : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                              }`}>
                              <Icon size={14} className={isActive ? 'text-white' : 'text-gray-400'} />
                              <span>{cat.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                      Annuler
                    </Button>
                    <button
                      type="button"
                      onClick={() => {
                        if (onAskAbra) {
                          onAskAbra({ contact: selectedContact, message: newMessage, isNew: true });
                          setShowCreateModal(false);
                        } else {
                          toast.info('Demande à Abra de t\'aider à rédiger ton message !');
                        }
                      }}
                      className="px-4 py-2 rounded-lg font-semibold text-white flex items-center gap-2"
                      style={{ background: 'linear-gradient(145deg, #E8955C 0%, #D4784A 100%)' }}
                    >
                      <Sparkles size={16} />
                      Demander à Abra
                    </button>
                    <Button
                      onClick={handleSendNewMessage}
                      disabled={!selectedContact || !newMessage.trim()}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      Envoyer
                    </Button>
                  </div>
                </div>
              </motion.div>
            </div>
          )
        )}
      </AnimatePresence>

      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {/* POPUP INVITATIONS - Style Premium/Luxe harmonisé */}
      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showInvitationsPopup && (
          <div className="fixed inset-0 md:left-[288px] z-[10002] flex items-center justify-center p-4 bg-black/60">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl max-h-[85vh] flex flex-col"
              data-testid="invitations-popup"
            >
              {/* Header du popup */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0 bg-gradient-to-r from-[#1e3a5f] to-[#2d4a6f]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                    <Mail size={20} className="text-orange-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Invitations reçues</h3>
                    <p className="text-xs text-white/70">
                      {invitations.length} demande{invitations.length > 1 ? 's' : ''} de conversation
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowInvitationsPopup(false)} 
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                  data-testid="close-invitations-popup"
                >
                  <X size={20} className="text-white" />
                </button>
              </div>

              {/* Corps du popup */}
              <div className="flex-1 overflow-y-auto">
                {loadingInvitations ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-3 border-orange-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : invitations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
                      <UserCheck size={28} className="text-gray-300" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Aucune invitation</h4>
                    <p className="text-sm text-gray-500 max-w-xs">
                      Tu n'as pas d'invitation en attente pour le moment. Quand quelqu'un voudra te contacter sur Abracada Chat, tu verras sa demande ici.
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {invitations.map((invitation) => (
                      <div 
                        key={invitation.id} 
                        className="p-4 hover:bg-gray-50 transition-colors"
                        data-testid={`invitation-card-${invitation.id}`}
                      >
                        {/* Info de l'expéditeur */}
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#1e3a5f] to-[#2d4a6f] flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                            {invitation.sender?.avatar ? (
                              <img 
                                src={invitation.sender.avatar} 
                                alt={invitation.sender.name}
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : (
                              invitation.sender?.name?.charAt(0) || 'U'
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <h4 className="font-semibold text-gray-900 truncate">
                                {invitation.sender?.name || 'Utilisateur'}
                              </h4>
                              {invitation.sender?.user_type === 'professionnel' && (
                                <span className="flex-shrink-0 px-2 py-0.5 bg-[#1e3a5f] text-white text-[10px] font-semibold rounded-full flex items-center gap-1">
                                  <BadgeCheck size={10} />
                                  PRO
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 truncate">
                              {invitation.sender?.email}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              {formatRelativeTime(invitation.created_at)}
                            </p>
                          </div>
                        </div>

                        {/* Message d'invitation */}
                        {invitation.message && (
                          <div className="mb-4 p-3 bg-gray-50 rounded-xl border border-gray-100">
                            <p className="text-sm text-gray-700 italic">
                              "{invitation.message}"
                            </p>
                          </div>
                        )}

                        {/* Boutons d'action */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleAcceptInvitation(invitation.id)}
                            disabled={processingInvitation === invitation.id}
                            data-testid={`accept-invitation-${invitation.id}`}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {processingInvitation === invitation.id ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <>
                                <UserCheck size={16} />
                                Accepter
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => handleRefuseInvitation(invitation.id)}
                            disabled={processingInvitation === invitation.id}
                            data-testid={`refuse-invitation-${invitation.id}`}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {processingInvitation === invitation.id ? (
                              <div className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <>
                                <UserX size={16} />
                                Refuser
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer informatif */}
              <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 flex-shrink-0">
                <p className="text-xs text-gray-500 text-center">
                  En acceptant, une nouvelle conversation sera créée avec cette personne.
                </p>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
